import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 39.5, lng: -8.0 };

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    background: '#e8edf3',
    overflow: 'hidden',
  },
  info: {
    position: 'absolute',
    top: 14, left: 14, right: 14,
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '10px 18px',
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 14,
    boxShadow: '0 10px 30px -8px rgba(0,0,0,0.25), 0 1px 0 0 rgba(255,255,255,0.1) inset',
    zIndex: 5,
    pointerEvents: 'none',
    color: '#fff',
  },
  infoTitle: {
    fontSize: '0.8rem', fontWeight: 700, color: '#fff',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    flex: 1, letterSpacing: '0.01em',
    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  infoDivider: {
    width: 1, height: 18,
    background: 'rgba(255,255,255,0.25)',
  },
  infoMeta: {
    fontSize: '0.7rem', fontWeight: 700,
    whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '3px 9px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.95)',
    textShadow: '0 1px 2px rgba(0,0,0,0.25)',
  },
  infoKm: {
    background: 'rgba(59,130,246,0.35)',
    border: '1px solid rgba(147,197,253,0.45)',
  },
  infoDur: {
    background: 'rgba(245,158,11,0.35)',
    border: '1px solid rgba(252,211,77,0.45)',
  },
  infoOrigin: {
    background: 'rgba(34,197,94,0.28)',
    border: '1px solid rgba(134,239,172,0.45)',
  },
  infoDest: {
    background: 'rgba(239,68,68,0.28)',
    border: '1px solid rgba(252,165,165,0.45)',
  },
  empty: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 8,
    color: '#94a3b8',
    pointerEvents: 'none',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(255,255,255,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 6,
  },
}));

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: { position: 9 }, // RIGHT_BOTTOM = 9 (avoid depending on window.google)
  clickableIcons: false,
  gestureHandling: 'cooperative',
  styles: [
    // Soft, minimal styling — suppress POIs, transit, business clutter to reduce visual noise
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  ],
};

function formatKm(m) {
  if (!m) return null;
  return `${(m / 1000).toFixed(0)} km`;
}

function formatDuration(sec) {
  if (!sec) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  return `${m}m`;
}

/**
 * Minimal preview map for the selected route.
 * Shows only: polyline + origin + destination. No traffic, no vehicles, no stops,
 * no POIs, no controls. The route geometry is pre-sampled to a cap of 400 points
 * to keep rendering light even for long routes.
 */
function RoutePreviewMap({ route, loading }) {
  const classes = useStyles();
  const mapRef = useRef(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  // Down-sample the geometry for performance on large routes.
  const path = useMemo(() => {
    const coords = route?.routeData?.computedRoute?.geometry?.coordinates || [];
    if (coords.length === 0) return [];
    const MAX_POINTS = 400;
    if (coords.length <= MAX_POINTS) {
      return coords.map(c => ({ lat: c[1], lng: c[0] }));
    }
    const step = Math.ceil(coords.length / MAX_POINTS);
    const out = [];
    for (let i = 0; i < coords.length; i += step) {
      out.push({ lat: coords[i][1], lng: coords[i][0] });
    }
    // Ensure last point included
    const last = coords[coords.length - 1];
    out.push({ lat: last[1], lng: last[0] });
    return out;
  }, [route]);

  const origin = route?.origin;
  const destination = route?.destination;

  const onLoadMap = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Fit bounds whenever path changes
  useEffect(() => {
    if (!mapRef.current || !isLoaded || path.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    mapRef.current.fitBounds(bounds, { top: 60, bottom: 40, left: 40, right: 40 });
  }, [path, isLoaded]);

  if (!isLoaded) {
    return (
      <Box className={classes.root}>
        <Box className={classes.empty}>
          <CircularProgress size={24} style={{ color: '#6366f1' }} />
          <Typography style={{ fontSize: '0.74rem' }}>A carregar mapa…</Typography>
        </Box>
      </Box>
    );
  }

  const hasRoute = !!route;
  const km = route?.totalDistanceM ? formatKm(route.totalDistanceM) : null;
  const duration = route?.totalDurationS ? formatDuration(route.totalDurationS) : null;

  return (
    <Box className={classes.root}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={DEFAULT_CENTER}
        zoom={6}
        options={MAP_OPTIONS}
        onLoad={onLoadMap}
      >
        {path.length > 1 && (
          <Polyline
            path={path}
            options={{
              strokeColor: '#3b82f6',
              strokeOpacity: 0.9,
              strokeWeight: 5,
              geodesic: false,
              clickable: false,
            }}
          />
        )}
        {origin?.coordinates && (
          <Marker
            position={{ lat: origin.coordinates[1], lng: origin.coordinates[0] }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: '#22c55e',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            title={origin.name || 'Origem'}
          />
        )}
        {destination?.coordinates && (
          <Marker
            position={{ lat: destination.coordinates[1], lng: destination.coordinates[0] }}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: '#ef4444',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            title={destination.name || 'Destino'}
          />
        )}
      </GoogleMap>

      {hasRoute && (
        <Box className={classes.info}>
          <Typography className={classes.infoTitle}>
            {route.name || `Rota #${route.id}`}
          </Typography>
          {km && <Typography className={`${classes.infoMeta} ${classes.infoKm}`}>{km}</Typography>}
          {duration && <Typography className={`${classes.infoMeta} ${classes.infoDur}`}>{duration}</Typography>}
          <Box className={classes.infoDivider} />
          <Typography className={`${classes.infoMeta} ${classes.infoOrigin}`}>
            <RoomIcon style={{ fontSize: 12 }} />
            {origin?.name?.split(',')[0] || '—'}
          </Typography>
          <Typography className={`${classes.infoMeta} ${classes.infoDest}`}>
            <FlagIcon style={{ fontSize: 12 }} />
            {destination?.name?.split(',')[0] || '—'}
          </Typography>
        </Box>
      )}

      {!hasRoute && !loading && (
        <Box className={classes.empty}>
          <MapOutlinedIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
          <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>
            Seleccione uma rota para ver a pré-visualização
          </Typography>
        </Box>
      )}

      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress size={24} style={{ color: '#6366f1' }} />
        </Box>
      )}
    </Box>
  );
}

export default React.memo(RoutePreviewMap);
